import React, { useMemo } from "react";
import Select from "react-select";
import {
  TextInput,
  TextArea,
  FormField,
  StepHeader,
} from "../activity-components/FormComponents";
import { AddressSearchMap } from "../activity-components/AddressSearchMap";
import { StepProps, ActivityFormProps, SelectOption } from "../types";
import {
  ALL_DIGITAL_VALUE,
  ALL_DIGITAL_LABEL,
  MAX_NAME,
  MAX_DESC,
} from "../utils/ValidationUtil";

type Props = StepProps &
  Pick<
    ActivityFormProps,
    "descriptionOfTab2" | "activityTypes" | "ageGroups" | "municipalities" | "svgSearch" | "descriptionOfAddressField"
  > & {
    onChange: (
      k: keyof import("../types").FormState,
    ) => (e: React.ChangeEvent<any>) => void;
    onMultiChange: (
      k: "municipalities" | "ages" | "types",
    ) => (vals: SelectOption[]) => void;
  };

export function Step2({
  data,
  validationErrors,
  clearValidationError,
  stepFocusRef,
  descriptionOfTab2,
  activityTypes,
  ageGroups,
  municipalities,
  onChange,
  onMultiChange,
  onBlur,
  setData,
  svgSearch,
  descriptionOfAddressField
}: Props) {

const typeOptions = useMemo(() => activityTypes.map(x => 
  ({ label: x.text, value: x.value })), 
  [activityTypes]);
  
  const ageOptions = useMemo(
    () => ageGroups.map((x) => ({ label: x, value: x })),
    [ageGroups],
  );
  const municipalityOptions = useMemo(
    () =>
      municipalities.map((x) => ({
        label: x === ALL_DIGITAL_VALUE ? ALL_DIGITAL_LABEL : x,
        value: x,
      })),
    [municipalities],
  );

  const selectedMunicipalities = useMemo(() => {
    if (data.municipalities.includes(ALL_DIGITAL_VALUE)) {
      return [{ label: ALL_DIGITAL_LABEL, value: ALL_DIGITAL_VALUE }];
    }
    return municipalityOptions.filter((o) =>
      data.municipalities.includes(o.value),
    );
  }, [data.municipalities, municipalityOptions]);

  const isMunicipalityOptionDisabled = useMemo(
    () => (option: SelectOption) =>
      data.municipalities.includes(ALL_DIGITAL_VALUE) &&
      option.value !== ALL_DIGITAL_VALUE,
    [data.municipalities],
  );

  return (
    <>
      <StepHeader
        focusRef={stepFocusRef}
        ariaLabel="Steg 2: Om aktiviteten"
        description={descriptionOfTab2}
      />

      <TextInput
        label="Namn på aktivitetsarrangör"
        value={data.name}
        onChange={(e) => {
          onChange("name")(e);
          clearValidationError("name");
        }}
        onBlur={onBlur("name")}
        required
        hasError={validationErrors.has("name")}
        id="name"
        helpText={`Namnet får vara max ${MAX_NAME} tecken långt.`}
      />

      <TextArea
        label="Kort beskrivning av aktivitet"
        value={data.description}
        onChange={(e) => {
          onChange("description")(e);
          clearValidationError("description");
        }}
        onBlur={onBlur("description")}
        maxLength={MAX_DESC}
        rows={5}
        required
        hasError={validationErrors.has("description")}
        id="description"
        helpText={
          <>
            Text som beskriver er aktivitet/aktiviteter och eventuella
            målgrupper. Exempel: gravid, mamma, familj, funktionsnedsättning,
            ickebinär, kvinna, man. Max {MAX_DESC} tecken.
          </>
        }
      />

      <FormField
        label="Typ av aktivitet"
        inputId="types"
        required
        hasError={validationErrors.has("types")}
      >
        <Select
          classNamePrefix="multi-select"
          inputId="types"
          isMulti
          options={typeOptions}
          value={typeOptions.filter((o) =>
            Array.isArray(data.types) ? data.types.includes(o.value) : false,
          )}
          onChange={(vals) => {
            onMultiChange("types")(vals as SelectOption[]);
            clearValidationError("types");
          }}
          onBlur={() => onBlur("types")({} as any)}
          aria-required="true"
          aria-invalid={validationErrors.has("types") || undefined}
        />
      </FormField>

      <FormField
        label="Ålder"
        inputId="ages"
        required
        hasError={validationErrors.has("ages")}
      >
        <Select
          classNamePrefix="multi-select"
          inputId="ages"
          isMulti
          options={ageOptions}
          value={ageOptions.filter((o) => data.ages.includes(o.value))}
          onChange={(vals) => {
            onMultiChange("ages")(vals as SelectOption[]);
            clearValidationError("ages");
          }}
          onBlur={() => onBlur("ages")({} as any)}
          aria-required={true}
          aria-invalid={validationErrors.has("ages") || undefined}
        />
      </FormField>

      <FormField
        label="Kommun"
        inputId="municipalities"
        required
        hasError={validationErrors.has("municipalities")}
      >
        <Select
          classNamePrefix="multi-select"
          inputId="municipalities"
          isMulti
          options={municipalityOptions}
          value={selectedMunicipalities}
          onChange={(vals) => {
            onMultiChange("municipalities")(vals as SelectOption[]);
            clearValidationError("municipalities");
          }}
          onBlur={() => onBlur("municipalities")({} as any)}
          isOptionDisabled={isMunicipalityOptionDisabled}
          aria-required="true"
          aria-invalid={validationErrors.has("municipalities") || undefined}
        />
      </FormField>

      <TextArea
        label="Prova-på"
        value={data.tryout}
        onChange={onChange("tryout")}
        helpText="Beskriv hur man kan prova på er aktivitet."
      />

      <TextArea
        label="Med Fysisk aktivitet på recept, FaR"
        value={data.far}
        onChange={onChange("far")}
        helpText="Beskriv om ni har erbjudande till personer som har FaR, tex rabatt, extra prova på, extra instruktion."
      />

      <label htmlFor="onlineOrNoSpecificAddress">
        <input
          type="checkbox"
          id="onlineOrNoSpecificAddress"
          checked={data.onlineOrNoSpecificAddress}
          onChange={(e) =>
            setData((s) => ({
              ...s,
              onlineOrNoSpecificAddress: e.target.checked,
              addresses: e.target.checked ? [] : s.addresses,
              address: e.target.checked ? "" : s.address,
              latitude: e.target.checked ? undefined : s.latitude,
              longitude: e.target.checked ? undefined : s.longitude,
            }))
          }
        />
        Är din aktivitet online eller har den ingen specifik adress?
      </label>

      {!data.onlineOrNoSpecificAddress && (
        <AddressSearchMap
          addresses={data.addresses}
          onAddressesChange={(addresses) =>
            setData((s) => ({
              ...s,
              addresses,
              address: addresses[0]?.address ?? "",
              latitude: addresses[0]?.latitude,
              longitude: addresses[0]?.longitude,
            }))
          }
          svgSearch={svgSearch}
          descriptionOfAddressField={descriptionOfAddressField}
        />
      )}

      <label htmlFor="fritidskortet">
        <input
          type="checkbox"
          id="fritidskortet"
          checked={!!data.fritidskortet}
          onChange={(e) =>
            setData((s) => ({ ...s, fritidskortet: e.target.checked }))
          }
        />
        <span className="visually-hidden">Alternativ:</span>
        Vi är anslutna till Fritidskortet.se.
      </label>
    </>
  );
}
